import { useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { externalSystemService } from '../../services/externalSystemService';
import { useNotificationStore } from '../../store/notificationStore';
import type { ApiExecutionResult } from '../../types/externalSystem';

interface ApiMethodTesterProps {
  systemId: string;
  methodId: string;
}

/**
 * API method tester component for testing API method execution.
 */
export const ApiMethodTester = ({ systemId, methodId }: ApiMethodTesterProps) => {
  const { addNotification } = useNotificationStore();

  const [parametersJson, setParametersJson] = useState('{}');
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<ApiExecutionResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleTest = async () => {
    setError(null);
    setResult(null);

    // Validate JSON
    let parameters: Record<string, any> = {};
    if (parametersJson.trim()) {
      try {
        parameters = JSON.parse(parametersJson.trim());
      } catch {
        setError('Parameters JSON must be valid JSON');
        return;
      }
    }

    setTesting(true);
    try {
      const testResult = await externalSystemService.testApiMethod(systemId, methodId, { parameters });
      setResult(testResult);
      if (testResult.isSuccess) {
        addNotification('API test executed successfully', 'success');
      } else {
        addNotification(`API test failed: ${testResult.errorMessage}`, 'error');
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to test API method';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setTesting(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Test API Method
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <TextField
            label="Parameters (JSON)"
            fullWidth
            multiline
            rows={6}
            value={parametersJson}
            onChange={(e) => setParametersJson(e.target.value)}
            helperText="JSON object with parameters for the API call"
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Button
            variant="contained"
            startIcon={testing ? <CircularProgress size={20} /> : <PlayIcon />}
            onClick={handleTest}
            disabled={testing}
          >
            {testing ? 'Testing...' : 'Test API Method'}
          </Button>
        </Grid>

        {error && (
          <Grid size={{ xs: 12 }}>
            <Alert severity="error">{error}</Alert>
          </Grid>
        )}

        {result && (
          <Grid size={{ xs: 12 }}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={2}>
                  {result.isSuccess ? (
                    <SuccessIcon color="success" />
                  ) : (
                    <ErrorIcon color="error" />
                  )}
                  <Typography variant="subtitle1">
                    Test Result - HTTP {result.statusCode} ({result.durationMs}ms)
                  </Typography>
                  <Chip
                    label={result.isSuccess ? 'Success' : 'Failed'}
                    color={result.isSuccess ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  {result.errorMessage && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {result.errorMessage}
                    </Alert>
                  )}

                  {result.responseBody && (
                    <Box mb={2}>
                      <Typography variant="subtitle2" gutterBottom>
                        Response Body:
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={10}
                        value={result.responseBody}
                        InputProps={{ readOnly: true }}
                        sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                      />
                    </Box>
                  )}

                  {Object.keys(result.responseHeaders).length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Response Headers:
                      </Typography>
                      <Box component="pre" sx={{ bgcolor: 'grey.100', p: 1, borderRadius: 1, fontSize: '0.75rem' }}>
                        {JSON.stringify(result.responseHeaders, null, 2)}
                      </Box>
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};
